export const APP_NAME = "CoyKoi";
export const APP_VERSION = "1.0.0";

/** New app. Port 3000 is used by other apps on this machine — see AGENTS.md. */
export const DEFAULT_PORT = 8888;

/** Vite dev server, proxies /api and /auth to DEFAULT_PORT. */
export const WEB_DEV_PORT = 5173;

/**
 * Anything that can carry custom fields, a status, or genre assignments.
 * Polymorphic tables key on (entity_type, entity_id).
 */
export const ENTITY_TYPES = [
  "track",
  "album",
  "artist",
  "playlist",
  "folder",
  "local_playlist",
  "genre",
] as const;

/** The content pipeline, carried over from v0. Order drives the board columns. */
export const STATUS_OPTIONS = [
  { id: "none", label: "None / not started" },
  { id: "pending", label: "Pending" },
  { id: "working", label: "Working on it" },
  { id: "video_edit", label: "Video edit" },
  { id: "published", label: "Published" },
  { id: "paused", label: "Paused" },
  { id: "cancelled", label: "Cancelled" },
] as const;

export const STATUS_IDS = STATUS_OPTIONS.map((s) => s.id) as unknown as readonly [
  "none",
  "pending",
  "working",
  "video_edit",
  "published",
  "paused",
  "cancelled",
];

/** Custom field types. `config` shape depends on this — see schemas/field.ts. */
export const FIELD_TYPES = [
  "text",
  "longtext",
  "number",
  "date",
  "url",
  "select",
  "multiselect",
  "checkbox",
  /** ISO 3166-1 alpha-2 (or `OTHER`); see `#shared/countries.ts`. */
  "country",
] as const;

/**
 * Who asserted a genre. In the primary key of entity_genres so a provider
 * refresh can never overwrite a user assignment.
 */
export const GENRE_SOURCES = ["user", "spotify", "lastfm", "musicbrainz"] as const;

export const SAVED_VIEW_LAYOUTS = ["table", "board"] as const;

/** Local entity id prefixes. Spotify entities keep their own ids. */
export const ID_PREFIXES = {
  user: "usr",
  folder: "fld",
  local_playlist: "lpl",
  genre: "gen",
  field_def: "fd",
  saved_view: "vw",
  graph_path: "gph",
  idea: "ide",
  idea_card: "icd",
  research_category: "rca",
  research_ingest: "rin",
} as const;

/** Needed to create a playlist on the user’s Spotify account. */
export const SPOTIFY_PLAYLIST_MODIFY_SCOPES = [
  "playlist-modify-private",
  "playlist-modify-public",
] as const;

/**
 * Scopes requested at login. Includes playlist-modify so workspace playlists
 * can be created on Spotify. Existing connections must reconnect once.
 */
export const SPOTIFY_SCOPES = [
  "user-read-private",
  "user-read-email",
  "user-library-read",
  "user-top-read",
  "user-read-recently-played",
  "user-follow-read",
  "playlist-read-private",
  "playlist-read-collaborative",
  ...SPOTIFY_PLAYLIST_MODIFY_SCOPES,
  "streaming",
  "user-read-playback-state",
  "user-modify-playback-state",
] as const;

/** True when a stored token string already includes playlist-modify. */
export function tokenHasPlaylistModifyScope(scope: string | null | undefined): boolean {
  if (!scope) return false;
  const granted = new Set(scope.split(/[\s,]+/).filter((part) => part.length > 0));
  return SPOTIFY_PLAYLIST_MODIFY_SCOPES.some((needed) => granted.has(needed));
}

export const SPOTIFY_SCOPE_STRING = SPOTIFY_SCOPES.join(" ");

/** Refresh the access token this many ms before it actually expires. */
export const TOKEN_REFRESH_SKEW_MS = 60_000;

/** How long an OAuth `state` is valid — long enough to finish the Spotify dialog. */
export const OAUTH_STATE_TTL_MS = 10 * 60 * 1000;

/**
 * Phase 5 sync budgets. Hard caps so a bug cannot burn the Dev Mode day.
 * Sync only mirrors liked + playlists (album covers ride those payloads).
 * Never per-track GETs, never Spotify /artists enrichment.
 */
export const SYNC_PAGE_SIZE = 50;
/** Default max Spotify Web API calls for a full sync run. */
export const SYNC_DEFAULT_BUDGET = 500;
/**
 * Pause between Spotify pages. Slow on purpose (dev app) — prefer finishing
 * over racing into a multi-hour QUOTA_EXCEEDED lockout.
 */
export const SYNC_PAGE_DELAY_MS = 2_000;

/** Per-scope call budgets — high enough for a normal library; pace is the real brake. */
export const SYNC_SCOPE_BUDGETS = {
  liked: 250,
  library: 500,
  full: 500,
} as const;

export type EntityType = (typeof ENTITY_TYPES)[number];
export type StatusId = (typeof STATUS_IDS)[number];
export type FieldType = (typeof FIELD_TYPES)[number];
export type GenreSource = (typeof GENRE_SOURCES)[number];
export type SavedViewLayout = (typeof SAVED_VIEW_LAYOUTS)[number];
export type IdPrefix = (typeof ID_PREFIXES)[keyof typeof ID_PREFIXES];
